import { useState, useRef } from 'react'
import { Languages, Edit, Trash2, Save, Plus } from 'lucide-react'
import { X as XIcon } from 'lucide-react'
import { talents, libraries } from '../../services/apiService'
import toast from 'react-hot-toast'
import { Autocomplete } from '@anyemp/ui-kit'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'
import ConfirmDialog from '../UI/ConfirmDialog'

const LanguagesSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const talentType = talent?.talent_type

  const canCreate = hasPermission('talent-service', `${talentType}_languages`, 'create')
  const canUpdate = hasPermission('talent-service', `${talentType}_languages`, 'update')
  const canDelete = hasPermission('talent-service', `${talentType}_languages`, 'delete')

  const [showForm, setShowForm] = useState(false)
  const [editingLang, setEditingLang] = useState(null)
  const [formData, setFormData] = useState({ language_id: null, level_id: null })
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const [langOptions, setLangOptions] = useState([])
  const [levelOptions, setLevelOptions] = useState([])
  const [optionsLoading, setOptionsLoading] = useState(false)
  const optionsLoadedRef = useRef(false)

  const loadLangOptions = async () => {
    if (optionsLoadedRef.current) return
    optionsLoadedRef.current = true
    setOptionsLoading(true)
    try {
      const [langsRes, levelsRes] = await Promise.all([
        libraries.getLanguages(),
        libraries.getLanguageLevels()
      ])
      const norm = (r) => Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : []
      setLangOptions(norm(langsRes).map(l => ({ value: String(l.id), label: l.name, iso2: l.iso2 })))
      setLevelOptions(norm(levelsRes).map(l => ({ value: String(l.id), label: l.name, short_name: l.short_name })))
    } catch (err) {
      console.error('Error loading language options:', err)
      optionsLoadedRef.current = false
    } finally {
      setOptionsLoading(false)
    }
  }

  const handleAdd = () => {
    loadLangOptions()
    setEditingLang(null)
    setFormData({ language_id: null, level_id: null })
    setShowForm(true)
  }

  const handleEdit = (item) => {
    loadLangOptions()
    setEditingLang(item)
    setFormData({ language_id: item.language_id, level_id: item.level_id || null })
    setShowForm(true)
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingLang(null)
    setFormData({ language_id: null, level_id: null })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editingLang) {
        const result = await talents.updateTalentLanguage(talentId, editingLang.id, formData)
        const saved = result?.data || result
        setTalent(prev => ({
          ...prev,
          languages: (prev.languages || []).map(l => l.id === editingLang.id ? { ...l, ...formData, ...saved } : l)
        }))
        toast.success('Language updated', { duration: 2000, position: 'bottom-right' })
      } else {
        const result = await talents.addTalentLanguage(talentId, formData)
        const saved = result?.data || result
        setTalent(prev => ({
          ...prev,
          languages: [...(prev.languages || []), saved]
        }))
        toast.success('Language added', { duration: 2000, position: 'bottom-right' })
      }
      handleCancel()
    } catch (err) {
      toast.error('Failed to save language', { duration: 2000, position: 'bottom-right' })
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    setSaving(true)
    try {
      await talents.deleteTalentLanguage(talentId, deleteConfirm.id)
      setTalent(prev => ({
        ...prev,
        languages: (prev.languages || []).filter(l => l.id !== deleteConfirm.id)
      }))
      toast.success('Language deleted', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      toast.error('Failed to delete language', { duration: 2000, position: 'bottom-right' })
    } finally {
      setSaving(false)
    }
  }

  const langForm = (
    <form key="lang-form" onSubmit={handleSubmit} className="p-4 border border-light-border-primary dark:border-dark-border-primary rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-tertiary space-y-3">
      <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
        {editingLang ? 'Edit language' : 'New language'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Language *</label>
          <Autocomplete
            fullWidth
            value={formData.language_id ? String(formData.language_id) : ''}
            onChange={(v) => setFormData(prev => ({ ...prev, language_id: v ? parseInt(v) : null }))}
            options={langOptions}
            placeholder={optionsLoading ? 'Loading…' : 'Select language'}
            disabled={optionsLoading}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Level *</label>
          <Autocomplete
            fullWidth
            value={formData.level_id ? String(formData.level_id) : ''}
            onChange={(v) => setFormData(prev => ({ ...prev, level_id: v ? parseInt(v) : null }))}
            options={levelOptions}
            placeholder={optionsLoading ? 'Loading…' : 'Select level'}
            disabled={optionsLoading}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving || optionsLoading} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-50">
          <Save className="h-3 w-3" /> {editingLang ? 'Update' : 'Add'}
        </button>
        <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50">
          <XIcon className="h-3 w-3" /> Cancel
        </button>
      </div>
    </form>
  )

  const langList = talent?.languages || []

  const cards = langList.map((item) => {
    if (editingLang?.id === item.id) return langForm
    const langName = item.language?.name
    const langIso2 = item.language?.iso2
    const levelLabel = item.level?.short_name || item.level?.name
    return (
      <div key={item.id} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary flex items-center gap-3">
        <div className="w-7 h-7 rounded flex items-center justify-center bg-gray-100 dark:bg-gray-800 shrink-0">
          <Languages className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
            {langName || `Language #${item.language_id}`}
          </span>
          {langIso2 && (
            <span className="text-xs font-mono text-light-text-tertiary dark:text-dark-text-tertiary">
              {langIso2}
            </span>
          )}
          {levelLabel && (
            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
              {levelLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {canUpdate && (
            <button
              onClick={() => handleEdit(item)}
              onMouseEnter={loadLangOptions}
              onFocus={loadLangOptions}
              disabled={saving}
              className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors disabled:opacity-50"
              title="Edit"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm(item)}
              disabled={saving}
              className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors disabled:opacity-50"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    )
  })

  return (
    <>
    <SectionBlock id="languages">
      <SectionHeader
        icon={Languages}
        title={langList.length > 0 ? `Languages (${langList.length})` : 'Languages'}
        action={canCreate ? (
          <button
            onClick={handleAdd}
            onMouseEnter={loadLangOptions}
            onFocus={loadLangOptions}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        ) : null}
      />
      {showForm && !editingLang && (
        <div className="mb-3">{langForm}</div>
      )}
      {cards.length > 0
        ? <div className="space-y-3">{cards}</div>
        : <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No languages specified</p>
      }
    </SectionBlock>

    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      confirmStyle="danger"
      title="Delete Language"
      message={deleteConfirm
        ? `Are you sure you want to delete ${deleteConfirm.language?.name || 'this language'}? This action cannot be undone.`
        : ''}
      confirmText="Delete"
      isLoading={saving}
    />
    </>
  )
}

export default LanguagesSection
